/* eslint-disable react/jsx-key */
import { useState, useEffect, useRef, MutableRefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { Box, TextField, Typography, Divider } from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  PlaylistAdd as PlaylistAddIcon,
  InsertPageBreak as InsertPageBreakIcon,
  Visibility as VisibilityIcon,
  TaskAlt as TaskAltIcon,
  Warning as WarningIcon,
} from '@mui/icons-material/';
import Checkbox from '@ui-elements/Checkbox';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useAppSelector, useAppDispatch } from '~/App/hooks';
import { fetchUserScreenVisibilitySettings } from '~/api';
import {
  finalizeOriginalFormSurvey,
  getPreviewDraft,
  updateQuestionsContent,
  getQuestionsContent,
  getDraftNgWords,
} from '~/apiTs';
import { BASE_FONT_FAMILY, BUTTON, FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { GMOASK_STATUS } from '~/constants/api';
import {
  CONTENT_TYPE,
  QUESTION_TYPE_ID,
  SURVEY_FORM_TYPE,
  ORIGINAL_FORM_VALIDATION,
} from '~/common/constants';
import {
  removeTagsOnAnswerLabel,
  removeTagsOnQuestionLabel,
  getOriginalFormsValidationMsg,
  escapeQuestionsContentLabel,
  unescapeQuestionsContentLabel,
} from '~/common/functional';
import {
  SectionProps,
  OriginalFormQuestionIdMap,
  OriginalFormUiDisplayProps,
  QuestionContentProps,
  SingleAnswerConfigProps,
  QuestionProps,
  QuestionBoxProps,
  SingleAnswerConfigAnswerProps,
  MultipleAnswerConfigAnswerProps,
  MultipleAnswerConfigProps,
  BasicInfoPropsContent,
  BasicInfoProps,
} from '~/common/types';
import { RestApiResponse } from '~/common/types/api';
import {
  PreviewSurveyResponse,
  GetQuestionsContentResponse,
  GetDraftNgWordsResponse,
  DraftNgWordsData,
} from '~/common/types/api/users';
import {
  PROGRESS_TRACKER_LABELS,
  PROGRESS_TYPE,
  SCREEN_VISIBILITY_SETTINGS,
  SCREEN_VISIBILITY_SETTINGS_IDS,
  SURVEY_PROGRESSES,
} from '~/constants';
import { ROUTES } from '~/constants/routes';
import { validateUtf8mb4 } from '~/constants/validationRules';
import HelpLink from '~/components/ui-parts/HelpLink';
import InputValidationErrorTooltip from '~/components/ui-parts/InputValidationErrorTooltip';
import PageLinkBar from '~/components/ui-parts/PageLinkBar';
import ProgressTracker from '~/components/ui-parts/ProgressTracker';
import QuestionBox from '../organisms/QuestionBox';
import DescriptionArea from '../organisms/DescriptionArea';
import DeleteOriginalFormContentModal from '../organisms/DeleteOriginalFormContentModal';
import FormValidationModal from '../organisms/FormValidationModal';
import PageBreakItem from '../organisms/PageBreakItem';
import NgChecklistModal from '../../NgChecklistModal';

const SurveyRegisterForm = styled.div`
  width: 768px;
  margin-left: 5%;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  text-align: left;
  font-size: ${FONT_SIZES.title1};
  gap: 16px;
  position: relative;
`;

const Title = styled(Typography)`
  font-size: ${FONT_SIZES.title1};
  line-height: 28px;
`;

const SubTitle = styled(Typography)`
  font-size: ${FONT_SIZES.title4};
  line-height: 28px;
`;

type ContentBoxProps = {
  $isValid?: boolean;
};
const ContentBox = styled.div<ContentBoxProps>`
  width: 768px;
  padding: 25px;
  margin-top: 32px;
  margin-right: 20px;
  background: ${PALETTE.white};
  display: flex;
  flex-direction: column;

  ${(props) => {
    if (props.$isValid === false) {
      return `
        border: 1px solid ${PALETTE.error.main};
      `;
    }
    return `
        border: 1px solid ${PALETTE.border.frame};
      `;
  }}
`;

const Flexwrap = styled.div`
  display: flex;
  align-items: center;
  margin-top: 7px;
`;

type ContentControlBoxProps = {
  $isValid?: boolean;
};
const ContentControlBox = styled.div<ContentControlBoxProps>`
  position: -webkit-sticky;
  position: sticky;
  top: 310px;
  right: calc(100% * 0.2);
  min-width: 172px;
  min-height: 311px;
  border: ${(props) =>
    props.$isValid ? `1px solid ${PALETTE.border.frame}` : `1px solid ${PALETTE.error.main}`};
  background: ${PALETTE.white};
  padding: ${(props) => (props.$isValid ? '27px 10px' : '0px')};
  box-shadow: 0px 3px 6px #00000029;
`;

const ValidationArea = styled.div`
  background: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  width: 172px;
  text-align: center;
  min-height: 100px;
  padding: 20px 25px;
  white-space: pre-line;
`;

const SurveyTitle = styled.div`
  font-size: ${FONT_SIZES.body5};
  padding-top: 12px;
  padding-bottom: 8px;
  .MuiInputBase-root {
    &:before {
      border: none;
    }
  }
`;

const SurveyDesc = styled.div`
  font-size: ${FONT_SIZES.bodyDefault};
  padding-top: 12px;
  padding-bottom: 8px;
  .MuiInputBase-root {
    &:before {
      border: none;
    }
  }
`;

const SurveyInfoBoxNotes = styled(Typography)`
  font-size: ${FONT_SIZES.bodyDefault};
  color: ${PALETTE.text.main};
`;

const SurveyInfoBoxSubNotes = styled(Typography)`
  font-size: ${FONT_SIZES.bodyDefault};
  color: ${PALETTE.text.sub};
  padding-top: 12px;
`;

const BasicInfo = styled.div`
  font-size: ${FONT_SIZES.bodyDefault};
  color: ${PALETTE.text.main};
  margin-left: -2px;
`;

const BasicInfoDesc = styled.div`
  font-size: ${FONT_SIZES.body3};
  color: ${PALETTE.text.sub};
  margin-left: 15px;
`;

const BasicInfoDescNote = styled.div`
  font-size: ${FONT_SIZES.note2};
  margin-left: 15px;
`;

type ChoiceBoxProps = {
  $marginLeft?: string;
  $marginTop?: string;
};
const ChoiceBox = styled.div<ChoiceBoxProps>`
  width: 220px;
  height: 78px;
  margin-top: ${(props) => props.$marginTop};
  margin-left: ${(props) => props.$marginLeft};
  padding: 12px 0;
`;

const AnswerBox = styled.div`
  display: flex;
  flex-direction: row;
`;

type ControlBoxItemProps = {
  $border?: boolean;
  $background?: string;
  $isValid?: boolean;
  $isActive?: boolean;
};

const ControlBoxItem = styled.div<ControlBoxItemProps>`
  display: flex;
  flex-direction: row;
  padding: 12px;
  margin: ${(props) => (props.$isValid ? '0 0 8px 0' : '10px')};
  background: ${(props) => (props.$background ? props.$background : PALETTE.white)};
  border: ${(props) => (props.$border ? `1px solid ${PALETTE.border.frame}` : 0)};
  border-radius: ${BUTTON.squareRoundBorder.radius};
  cursor: pointer;
  transition: opacity 0.4s;
  color: ${(props) => (props.$isActive ? `${PALETTE.info.main}` : '')};
  &:hover {
    opacity: 0.6;
  }
`;

type StyledTextFieldProps = {
  $isTitleValid?: boolean;
  $isDescValid?: boolean;
};
const StyledTextField = styled(TextField)<StyledTextFieldProps>`
  input[type='text'] {
    padding: 5px 0 5px 0px;
    font-size: ${FONT_SIZES.bodyDefault};
    color: ${PALETTE.text.main};

    ${(props) => {
      if (props.$isTitleValid === false || props.$isDescValid === false) {
        return `
        border-bottom: 1px solid ${PALETTE.error.main};
      `;
      }
      return `
        border-bottom: 1px solid ${PALETTE.border.inputBorderBottom};
      `;
    }}
  }
`;

const SurveyRegisterOriginalFormManual: React.FC<Record<string, never>> = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const originalForm = useAppSelector((state) => state.originalForm);
  const user = useAppSelector((state) => state.user);

  const [activeIdxRef, setActiveIdxRef] = useState(useRef<null | HTMLDivElement>(null));
  const [errorIdxRef, setErrorIdxRef] = useState(useRef<null | HTMLDivElement>(null));
  const titleAndDescRef = useRef<HTMLDivElement>(null);

  const [title, setTitle] = useState(originalForm.questionsContent.title);
  const [description, setDescription] = useState(originalForm.questionsContent.description);
  const [basicInfo, setBasicInfo] = useState<BasicInfoProps>(
    originalForm.questionsContent.basicInfo
  );
  const [contentsList, setContentsList] = useState(Array<OriginalFormUiDisplayProps>);

  const [questionIds, setQuestionIds] = useState<Array<OriginalFormQuestionIdMap>>([]);
  const [dndIds, setDndIds] = useState<Array<string>>([]);

  const [showNgChecklistModal, setShowNgChecklistModal] = useState(true);
  const [openNgChecklistModals, setOpenNgChecklistModals] = useState(false);

  const [isFormValid, setIsFormValid] = useState(true);
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isDescValid, setIsDescValid] = useState(true);
  const [ngWordsList, setNgWordsList] = useState<Array<DraftNgWordsData>>([]);

  const [isQuestionDeleteModalShow, setIsQuestionDeleteModalShow] = useState(false);
  const [isPageDeleteModalShow, setIsPageDeleteModalShow] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState(-1);

  const [activeElementIdx, setActiveElementIdx] = useState(-1);

  const [isCheckAge, setIsCheckAge] = useState(true);
  const [isCheckGender, setIsCheckGender] = useState(true);
  const [isCheckArea, setIsCheckArea] = useState(true);

  const [validationErrorModalMsg, setValidationErrorModalMsg] = useState('');

  const basicInfoChoices = t('registration.originalForm.manual.basicInfoBox.choices', {
    returnObjects: true,
  });
  const basicInfoAnswers = t('registration.originalForm.manual.basicInfoBox.answers', {
    returnObjects: true,
  });

  const links = [
    {
      label: `${t('pageTitles.user.surveyRegisterSelection.category')}   ${t(
        'pageTitles.user.surveyRegisterSelection.title'
      )}`,
      routePath: ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM,
    },
    {
      // For arrow to enable on display
      label: 'アンケート作成方法選択',
      routePath: '',
    },
  ];

  const handleRemoveIsValidAnswerProperty = (newQuestionsContent: QuestionContentProps): string => {
    const clonedQuestionContents = { ...newQuestionsContent };
    const { sections } = clonedQuestionContents;

    if (sections) {
      const updatedSections = sections.map((section) => {
        const { questions } = section;

        if (questions) {
          const updatedQuestions = questions.map((question) => {
            // eslint-disable-next-line no-unused-vars
            const { isQuestionValid, isGotoSectionValid, ...tmpQuestion } = question;
            if (
              question.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
              question.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN
            ) {
              if (tmpQuestion.singleAnswerConfig && tmpQuestion.singleAnswerConfig.answers) {
                const updatedAnswers = tmpQuestion.singleAnswerConfig.answers?.map(
                  // eslint-disable-next-line no-unused-vars
                  ({ isValid, isGotoValid, ...restAnswer }) => ({
                    ...restAnswer,
                    label: escapeQuestionsContentLabel(restAnswer.label),
                  })
                );
                const singleAnswerConfig = {
                  ...tmpQuestion.singleAnswerConfig,
                  answers: updatedAnswers,
                };
                tmpQuestion.singleAnswerConfig = singleAnswerConfig;
              }
            } else if (question.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER) {
              if (tmpQuestion.multipleAnswerConfig && tmpQuestion.multipleAnswerConfig.answers) {
                const updatedAnswers = tmpQuestion?.multipleAnswerConfig?.answers?.map(
                  // eslint-disable-next-line no-unused-vars
                  ({ isValid, ...restAnswer }) => ({
                    ...restAnswer,
                    label: escapeQuestionsContentLabel(restAnswer.label),
                  })
                );

                const multipleAnswerConfig = {
                  ...tmpQuestion.multipleAnswerConfig,
                  answers: updatedAnswers,
                };
                tmpQuestion.multipleAnswerConfig = multipleAnswerConfig;
              }
            }

            tmpQuestion.label = escapeQuestionsContentLabel(tmpQuestion.label);
            return tmpQuestion;
          });

          return { ...section, questions: updatedQuestions };
        }
        /* istanbul ignore next */
        return { ...section };
      });

      clonedQuestionContents.sections = updatedSections;
    }

    clonedQuestionContents.description = escapeQuestionsContentLabel(
      clonedQuestionContents.description
    );

    return JSON.stringify(clonedQuestionContents);
  };

  const updateSurveyJson = (newQuestionsContent: QuestionContentProps, onBlur = true) => {
    dispatch({
      type: 'originalForm/updated',
      payload: {
        questionsContent: newQuestionsContent,
      },
    });

    if (onBlur) {
      if (originalForm.id) {
        const questionsContent = handleRemoveIsValidAnswerProperty(newQuestionsContent);
        dispatch(
          updateQuestionsContent({
            surveyId: originalForm.id,
            questionsContent,
          })
        ) as unknown as PayloadAction<
          RestApiResponse,
          string,
          { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
          never
        >;
      }
    }
  };

  const createNewQuestionsContentSectionState = (newContentsList: OriginalFormUiDisplayProps[]) => {
    const sectionsList: Array<SectionProps> = [];
    let questionsList: Array<QuestionProps> = [];

    newContentsList.forEach((item) => {
      if (item.contentType !== CONTENT_TYPE.PAGE_BREAK && item.questionBoxProps) {
        questionsList.push(item.questionBoxProps.questionData);
      } else {
        sectionsList.push({
          goto: item.pageBreakItemProps.isGotoEndChecked ? 'END' : '',
          questions: questionsList,
        });
        questionsList = [];
      }
    });

    sectionsList.push({ goto: '', questions: questionsList });
    return {
      ...originalForm.questionsContent,
      sections: sectionsList,
    };
  };

  const getQuestionsCountWithoutBasicInfo = (
    newSurveyJson: QuestionContentProps = originalForm.questionsContent
  ) => {
    let questionCount = 0;
    newSurveyJson.sections?.forEach((section) => {
      questionCount += section.questions
        ? section.questions.filter((question) => question.type !== QUESTION_TYPE_ID.TEXT).length
        : /* istanbul ignore next */
          0;
    });
    return questionCount;
  };

  const getQuestionsCountWithBasicInfo = (
    newSurveyJson: QuestionContentProps = originalForm.questionsContent
  ) => {
    let totalQuestionCount = getQuestionsCountWithoutBasicInfo(newSurveyJson);
    Object.keys(newSurveyJson.basicInfo).forEach((basicInfoKey) => {
      totalQuestionCount +=
        (newSurveyJson.basicInfo as Record<string, BasicInfoPropsContent>)[basicInfoKey]
          .isActive === true
          ? 1
          : 0;
    });

    return totalQuestionCount;
  };

  const isQuestionsCountValid = (newQuestionsCount: number): boolean => {
    if (newQuestionsCount > ORIGINAL_FORM_VALIDATION.ALL_QUESTION_MAX_SIZE) {
      console.log('Total Question Count Across all sections invalid');
      setValidationErrorModalMsg(t('registration.originalForm.validationErrorModal.maxQuestion'));
      return false;
    }
    return true;
  };

  const handleGotoEndChange = (itemIdx: number, isGotoEndChecked: boolean) => {
    const newContentsList = [...contentsList];

    const gotoSectionIdx = newContentsList[itemIdx - 1].questionBoxProps?.currentSectionCount;
    if (
      newContentsList.filter(
        (content) =>
          content.questionBoxProps?.currentSectionCount === gotoSectionIdx &&
          content.contentType === CONTENT_TYPE.QUESTION_BOX
      ).length > 1
    ) {
      setValidationErrorModalMsg(
        t('registration.originalForm.validationErrorModal.gotoEndSectionError')
      );
      return;
    }

    newContentsList[itemIdx] = {
      ...newContentsList[itemIdx],
      pageBreakItemProps: {
        ...newContentsList[itemIdx].pageBreakItemProps,
        isGotoEndChecked: !isGotoEndChecked,
      },
    };

    const newSurveyJson = createNewQuestionsContentSectionState(newContentsList);
    updateSurveyJson(newSurveyJson);
  };

  const handleBasicInfoChange = (choiceKey: string, answerKey: string) => {
    const newBasicInfo = JSON.parse(JSON.stringify(basicInfo));
    newBasicInfo[choiceKey][answerKey] = !(
      (basicInfo as Record<string, BasicInfoPropsContent>)[choiceKey] as Record<string, boolean>
    )[answerKey];

    const newSurveyJson = {
      ...originalForm.questionsContent,
      basicInfo: newBasicInfo,
    };

    if (isQuestionsCountValid(getQuestionsCountWithBasicInfo(newSurveyJson))) {
      if (answerKey === 'isActive') {
        if (newBasicInfo[choiceKey][answerKey]) {
          if (choiceKey === 'age') setIsCheckAge(false);
          if (choiceKey === 'gender') setIsCheckGender(false);
          if (choiceKey === 'area') setIsCheckArea(false);
        } else {
          newBasicInfo[choiceKey] = {
            isActive: false,
            isRequired: false,
          };

          if (choiceKey === 'age') setIsCheckAge(true);
          if (choiceKey === 'gender') setIsCheckGender(true);
          if (choiceKey === 'area') setIsCheckArea(true);
        }
      }

      setBasicInfo(newBasicInfo);
      updateSurveyJson(newSurveyJson);
    }
  };

  const isCheckBoxDisable = (choiceKey: string, answerKey: string, item: BasicInfoPropsContent) => {
    if (item.isActive) {
      return false;
    }
    if (answerKey === 'isRequired') {
      // return checkBoxStatuses[choiceKey] ?: false
      if (choiceKey === 'gender') return isCheckGender;
      if (choiceKey === 'age') return isCheckAge;
      if (choiceKey === 'area') return isCheckArea;
    }
    return false;
  };

  const initSimpleAnswerConfig: SingleAnswerConfigProps = {
    random: false,
    gotoRequired: false,
    answers: Array(1).fill({
      label: '',
      goto: '',
      isValid: true,
      isGotoValid: true,
    }),
  };

  const initQuestionData: QuestionProps = {
    id: uuidv4(),
    type: QUESTION_TYPE_ID.SINGLE_ANSWER,
    label: '',
    required: false,
    singleAnswerConfig: initSimpleAnswerConfig,
    isQuestionValid: true,
    isGotoSectionValid: true,
  };

  const initDescriptionArea: QuestionProps = {
    id: uuidv4(),
    type: QUESTION_TYPE_ID.TEXT,
    label: '',
    isQuestionValid: true,
    required: false,
  };

  const setNewQuestionIds = (newQuestionsContent: QuestionContentProps) => {
    let questionCount = 0;
    if (newQuestionsContent.sections === undefined || newQuestionsContent.sections.length === 0) {
      return;
    }

    if (newQuestionsContent.sections) {
      const newQuestionIdList: Array<OriginalFormQuestionIdMap | null> =
        newQuestionsContent.sections
          .map((section) => {
            if (section.questions && Array.isArray(section.questions)) {
              const question = section.questions.find(
                (q: QuestionProps) => q.type !== QUESTION_TYPE_ID.TEXT
              );

              if (question && section.questions.indexOf(question) === 0) {
                const questionIdMap = {
                  questionId: question.id,
                  questionNum: questionCount + 1,
                };
                questionCount += section.questions.filter(
                  (q) => q.type !== QUESTION_TYPE_ID.TEXT
                ).length;
                return questionIdMap;
              }
              questionCount += section.questions.filter(
                (q) => q.type !== QUESTION_TYPE_ID.TEXT
              ).length;
              return null;
            }
            return null;
          })
          .filter((value: OriginalFormQuestionIdMap | null) => value !== null);

      setQuestionIds(newQuestionIdList as Array<OriginalFormQuestionIdMap>);
    }
  };

  const getContentsListFromQuestionsContent = (questionsContent: QuestionContentProps) => {
    // Count questions in originalForm questionContents
    let totalQuestions = 0;
    questionsContent.sections?.forEach((section) => {
      if (section.questions) {
        totalQuestions += section.questions.length;
        section.questions.forEach((question) => {
          if (question.type === QUESTION_TYPE_ID.TEXT) {
            totalQuestions -= 1;
          }
        });
      }
    });

    const newContentList: Array<OriginalFormUiDisplayProps> = [];
    let totalSection = 1;

    let questionCount = 0;
    if (questionsContent.sections !== undefined) {
      const { sections } = questionsContent;
      totalSection = sections.length;

      for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx++) {
        const { questions } = sections[sectionIdx];

        let isGotoAnswerChecked = false;
        const isGotoEndChecked = sections[sectionIdx].goto === 'END';

        if (questions) {
          for (let questionIdx = 0; questionIdx < questions.length; questionIdx++) {
            let singleAnswerConfigEscape = questions[questionIdx].singleAnswerConfig;
            let multipleAnswerConfigEscape = questions[questionIdx].multipleAnswerConfig;
            if (singleAnswerConfigEscape) {
              singleAnswerConfigEscape = {
                ...singleAnswerConfigEscape,
                answers: singleAnswerConfigEscape.answers.map((answer) => ({
                  ...answer,
                  label: unescapeQuestionsContentLabel(answer.label),
                })),
              };
            }
            if (multipleAnswerConfigEscape) {
              multipleAnswerConfigEscape = {
                ...multipleAnswerConfigEscape,
                answers: multipleAnswerConfigEscape.answers.map((answer) => ({
                  ...answer,
                  label: unescapeQuestionsContentLabel(answer.label),
                })),
              };
            }
            let questionLabel = questions[questionIdx].label;
            if (questions[questionIdx].type === QUESTION_TYPE_ID.TEXT) {
              questionLabel = unescapeQuestionsContentLabel(questionLabel);
            }

            const questionBoxProps: QuestionBoxProps = {
              currentSectionCount: sectionIdx + 1,
              totalSection,
              totalQuestions,
              questionData: {
                ...questions[questionIdx],
                label: questionLabel,
                singleAnswerConfig: singleAnswerConfigEscape,
                multipleAnswerConfig: multipleAnswerConfigEscape,
                isGotoSectionValid: true,
              },
              questionIdx: questionCount,
              id: questions[questionIdx].id,
              isGotoAnswerEnabled: !isGotoEndChecked,
            };

            if (questions[questionIdx].type === QUESTION_TYPE_ID.TEXT) {
              newContentList.push({
                contentType: CONTENT_TYPE.DESCRIPTION_BOX,
                questionBoxProps,
                pageBreakItemProps: {
                  isGotoEndEnabled: false,
                  isGotoEndChecked: false,
                },
                isActive: false,
                key: questions[questionIdx].id,
              });
            } else {
              newContentList.push({
                contentType: CONTENT_TYPE.QUESTION_BOX,
                isActive: false,
                pageBreakItemProps: {
                  isGotoEndEnabled: false,
                  isGotoEndChecked: false,
                },
                questionBoxProps,
                key: questions[questionIdx].id,
              });
              questionCount += 1;

              if (questions[questionIdx]?.singleAnswerConfig) {
                isGotoAnswerChecked =
                  isGotoAnswerChecked ||
                  questions[questionIdx].singleAnswerConfig?.gotoRequired === true;
              }
            }
          }
        }

        if (sectionIdx !== sections.length - 1) {
          newContentList.push({
            contentType: CONTENT_TYPE.PAGE_BREAK,
            pageBreakItemProps: {
              isGotoEndEnabled: !isGotoAnswerChecked,
              isGotoEndChecked,
            },
            key: uuidv4(),
          });
        }
      }

      /* istanbul ignore else */
      if (activeElementIdx === -1) {
        newContentList[0] = {
          ...newContentList[0],
          isActive: true,
        };
        setActiveElementIdx(0);
      } else {
        newContentList[activeElementIdx] = {
          ...newContentList[activeElementIdx],
          isActive: true,
        };
      }

      // Reassign pageBreakItemProps.isGotoEndChecked for questionBox by pageBreak in the same section
      let isGotoQuestionChecked = false;
      for (let index = newContentList.length - 1; index >= 0; index--) {
        if (newContentList[index].contentType === CONTENT_TYPE.PAGE_BREAK) {
          isGotoQuestionChecked = newContentList[index].pageBreakItemProps.isGotoEndChecked;
        }
        if (
          newContentList[index].contentType === CONTENT_TYPE.QUESTION_BOX ||
          newContentList[index].contentType === CONTENT_TYPE.DESCRIPTION_BOX
        ) {
          newContentList[index].pageBreakItemProps.isGotoEndChecked = isGotoQuestionChecked;
        }
      }

      setContentsList(newContentList);
    }

    return newContentList;
  };

  useEffect(() => {
    getContentsListFromQuestionsContent(originalForm.questionsContent);
    setNewQuestionIds(originalForm.questionsContent);
  }, [originalForm.questionsContent]);

  const addNewQuestion = () => {
    if (!isQuestionsCountValid(getQuestionsCountWithBasicInfo() + 1)) {
      return;
    }

    const existingContentsList = [...contentsList];
    const newContentsList: OriginalFormUiDisplayProps[] = existingContentsList.map((item) => ({
      ...item,
      isActive: false,
    }));

    const dataAfterNewQuestion =
      activeElementIdx + 1 < newContentsList.length
        ? newContentsList[activeElementIdx + 1]
        : undefined;

    let isGotoEndEnabled = true;
    let isGotoEndChecked = false;
    if (dataAfterNewQuestion && dataAfterNewQuestion.contentType === CONTENT_TYPE.PAGE_BREAK) {
      isGotoEndChecked = dataAfterNewQuestion.pageBreakItemProps.isGotoEndChecked;
      isGotoEndEnabled = dataAfterNewQuestion.pageBreakItemProps.isGotoEndEnabled;

      newContentsList[activeElementIdx + 1] = {
        ...dataAfterNewQuestion,
        pageBreakItemProps: { isGotoEndEnabled: true, isGotoEndChecked: false },
      };
    }

    let insertIdx = activeElementIdx + 1;
    newContentsList.splice(insertIdx, 0, {
      contentType: CONTENT_TYPE.PAGE_BREAK,
      pageBreakItemProps: {
        isGotoEndEnabled,
        isGotoEndChecked,
      },
      key: uuidv4(),
    });
    insertIdx += 1;

    newContentsList.splice(insertIdx, 0, {
      contentType: CONTENT_TYPE.QUESTION_BOX,
      isActive: true,
      key: initQuestionData.id,
      pageBreakItemProps: {
        isGotoEndEnabled: false,
        isGotoEndChecked: false,
      },
      questionBoxProps: {
        questionData: initQuestionData,
        isValidate: false,
        id: initQuestionData.id,
        // Initial values it will be updated on update of survey json
        currentSectionCount: -1,
        totalSection: -1,
        totalQuestions: -1,
        questionIdx: -1,
      },
    });

    setActiveElementIdx(insertIdx);
    const newSurveyJson = createNewQuestionsContentSectionState(newContentsList);
    updateSurveyJson(newSurveyJson);
  };

  const addDescriptionArea = () => {
    const existingContentsList = [...contentsList];

    const newContentsList: OriginalFormUiDisplayProps[] = existingContentsList.map((item) => ({
      ...item,
      isActive: false,
    }));

    const dataAfterDescArea =
      activeElementIdx + 1 < newContentsList.length
        ? newContentsList[activeElementIdx + 1]
        : undefined;

    let isGotoEndEnabled = true;
    let isGotoEndChecked = false;
    if (dataAfterDescArea && dataAfterDescArea.contentType === CONTENT_TYPE.PAGE_BREAK) {
      isGotoEndChecked = dataAfterDescArea.pageBreakItemProps.isGotoEndChecked;
      isGotoEndEnabled = dataAfterDescArea.pageBreakItemProps.isGotoEndEnabled;

      newContentsList[activeElementIdx + 1] = {
        ...dataAfterDescArea,
        pageBreakItemProps: {
          isGotoEndEnabled: true,
          isGotoEndChecked: false,
        },
      };
    }

    let insertIdx = activeElementIdx + 1;
    newContentsList.splice(insertIdx, 0, {
      contentType: CONTENT_TYPE.PAGE_BREAK,
      pageBreakItemProps: {
        isGotoEndEnabled,
        isGotoEndChecked,
      },
      key: uuidv4(),
    });
    insertIdx += 1;

    newContentsList.splice(insertIdx, 0, {
      contentType: CONTENT_TYPE.DESCRIPTION_BOX,
      isActive: true,
      key: initQuestionData.id,
      pageBreakItemProps: {
        isGotoEndEnabled: false,
        isGotoEndChecked: false,
      },
      questionBoxProps: {
        questionData: initDescriptionArea,
        isValidate: false,
        id: initQuestionData.id,
        // Initial values it will be updated on update of survey json
        currentSectionCount: -1,
        totalSection: -1,
        totalQuestions: -1,
        questionIdx: -1,
      },
    });

    setActiveElementIdx(insertIdx);
    const newSurveyJson = createNewQuestionsContentSectionState(newContentsList);
    updateSurveyJson(newSurveyJson);
  };

  const addNewPage = () => {
    const newContentsList = [...contentsList];
    const insertIdx = activeElementIdx + 1;

    // If insertIdx is already at the end of the list do not add new page break
    if (insertIdx >= newContentsList.length) {
      return;
    }

    // If the item in insertIdx is page break do not add
    if (
      newContentsList[insertIdx] &&
      newContentsList[insertIdx].contentType === CONTENT_TYPE.PAGE_BREAK
    ) {
      return;
    }

    // Add new page item
    newContentsList.splice(insertIdx, 0, {
      contentType: CONTENT_TYPE.PAGE_BREAK,
      pageBreakItemProps: {
        isGotoEndEnabled: true,
        isGotoEndChecked: false,
      },
      key: uuidv4(),
    });
    const activeIdx = newContentsList.findIndex((content) => content.isActive);
    setActiveElementIdx(activeIdx);
    const newSurveyJson = createNewQuestionsContentSectionState(newContentsList);
    updateSurveyJson(newSurveyJson);

    activeIdxRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  };

  const deletePage = (pageIdx: number) => {
    if (pageIdx > 0) {
      const dataBeforePageBreak = contentsList[pageIdx - 1].questionBoxProps;
      if (
        dataBeforePageBreak &&
        (dataBeforePageBreak.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
          dataBeforePageBreak.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN) &&
        dataBeforePageBreak.questionData.singleAnswerConfig?.gotoRequired
      ) {
        setValidationErrorModalMsg(
          t('registration.originalForm.validationErrorModal.deletePageBreak')
        );
        console.log('Cannot delete page');
        return;
      }

      const dataAfterPageBreak = contentsList[pageIdx + 1].questionBoxProps;
      if (
        dataAfterPageBreak &&
        (dataAfterPageBreak.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
          dataAfterPageBreak.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN) &&
        dataAfterPageBreak.questionData.singleAnswerConfig?.gotoRequired
      ) {
        setValidationErrorModalMsg(
          t('registration.originalForm.validationErrorModal.deletePageBreak')
        );
        return;
      }
      // Check if Goto is required for any of the question in the page
      let checkGotoRequire = false;
      contentsList.forEach((content) => {
        if (
          content.questionBoxProps &&
          (content.questionBoxProps?.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
            content.questionBoxProps?.questionData.type ===
              QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN) &&
          content.questionBoxProps?.questionData.singleAnswerConfig?.gotoRequired
        ) {
          const listAnswer = content.questionBoxProps?.questionData.singleAnswerConfig?.answers;
          for (let i = 0; i < listAnswer.length; i++) {
            if (
              contentsList[pageIdx + 1].questionBoxProps?.questionData.id === listAnswer[i].goto
            ) {
              checkGotoRequire = true;
              break;
            }
          }
        }
      });

      // when user tries to delete page break,
      // if the question after page break is set in other question's goto target, show error modal
      if (checkGotoRequire) {
        setValidationErrorModalMsg(
          t('registration.originalForm.validationErrorModal.deletePageBreakWithGoto')
        );
        return;
      }
    }

    // Get the data after page break to be deleted
    const dataAfterPageBreak = contentsList[pageIdx + 1]?.questionBoxProps;

    if (dataAfterPageBreak) {
      const questionId = dataAfterPageBreak.questionData.id;
      const isGotoSetForDataAfterPageBreak = contentsList.some((content) => {
        const contentQuestionData = content.questionBoxProps?.questionData;

        if (
          contentQuestionData &&
          contentQuestionData?.singleAnswerConfig &&
          contentQuestionData.singleAnswerConfig?.answers
        ) {
          return contentQuestionData.singleAnswerConfig.answers.some(
            (answer) => answer.goto === questionId
          );
        }
        return false;
      });

      if (isGotoSetForDataAfterPageBreak) {
        setValidationErrorModalMsg(
          t('registration.originalForm.validationErrorModal.deletePageBreakWithGoto')
        );
        return;
      }
    }
    // Find next contentType page break from deleteIdx
    const nextContentTypePageBreakIdx = contentsList.findIndex(
      (content, idx) => idx > pageIdx && content.contentType === CONTENT_TYPE.PAGE_BREAK
    );

    if (
      nextContentTypePageBreakIdx > -1 &&
      contentsList[nextContentTypePageBreakIdx].pageBreakItemProps.isGotoEndChecked
    ) {
      const currSectionQuestionCount = contentsList.filter(
        (content) =>
          content.questionBoxProps?.currentSectionCount ===
            contentsList[pageIdx - 1].questionBoxProps?.currentSectionCount &&
          content.contentType === CONTENT_TYPE.QUESTION_BOX
      ).length;

      const nextSectionCount = contentsList.filter(
        (content) =>
          content.questionBoxProps?.currentSectionCount ===
            contentsList[nextContentTypePageBreakIdx - 1].questionBoxProps?.currentSectionCount &&
          content.contentType === CONTENT_TYPE.QUESTION_BOX
      ).length;

      if (currSectionQuestionCount + nextSectionCount > 1) {
        setValidationErrorModalMsg(
          t('registration.originalForm.validationErrorModal.gotoEndSectionError')
        );
        return;
      }
    }
    setIsPageDeleteModalShow(true);
    setDeleteIdx(pageIdx);
  };

  const confirmPageDelete = () => {
    const newContentsList = [...contentsList];

    setIsPageDeleteModalShow(false);

    newContentsList.splice(deleteIdx, 1);
    const activeIdx = newContentsList.findIndex((content) => content.isActive);
    setActiveElementIdx(activeIdx);

    const newSurveyJson = createNewQuestionsContentSectionState(newContentsList);
    updateSurveyJson(newSurveyJson);

    activeIdxRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  };

  const updateQuestionContent = (
    newContent: QuestionProps,
    contentIdx: number,
    onBlur: boolean
  ) => {
    const existingContentList = [...contentsList];

    const newContentsList: OriginalFormUiDisplayProps[] = existingContentList.map(
      (item, itemIdx) => {
        if (itemIdx === contentIdx) {
          return {
            ...item,
            ...(item.questionBoxProps && {
              questionBoxProps: {
                ...item.questionBoxProps,
                questionData: newContent,
              },
            }),
          };
        }
        return { ...item };
      }
    );

    const activeIdx = existingContentList.findIndex((content) => content.isActive);
    setActiveElementIdx(activeIdx);

    const newSurveyJson = createNewQuestionsContentSectionState(newContentsList);
    updateSurveyJson(newSurveyJson, onBlur);
  };

  const duplicateQuestion = (duplicateIdx: number) => {
    if (!isQuestionsCountValid(getQuestionsCountWithBasicInfo() + 1)) {
      return;
    }

    const newId: string = uuidv4();
    const activeIdx = contentsList.findIndex((content) => content.isActive);
    setActiveElementIdx(activeIdx);
    const newContentsList = [...contentsList];

    const dataToDuplicate = newContentsList[duplicateIdx]?.questionBoxProps;
    const dataAfterDuplicate =
      duplicateIdx + 1 < newContentsList.length ? newContentsList[duplicateIdx + 1] : undefined;

    let isGotoEndEnabled = true;
    let isGotoEndChecked = false;
    if (dataAfterDuplicate && dataAfterDuplicate.contentType === CONTENT_TYPE.PAGE_BREAK) {
      isGotoEndChecked = dataAfterDuplicate.pageBreakItemProps.isGotoEndChecked;
      isGotoEndEnabled = dataAfterDuplicate.pageBreakItemProps.isGotoEndEnabled;

      newContentsList[duplicateIdx + 1] = {
        ...dataAfterDuplicate,
        pageBreakItemProps: {
          isGotoEndEnabled: true,
          isGotoEndChecked: false,
        },
      };
    }
    if (dataToDuplicate) {
      const dataToDuplicateSingleAnswerConfig = dataToDuplicate.questionData.singleAnswerConfig;
      const dataToDuplicateMultipleAnswerConfig = dataToDuplicate.questionData.multipleAnswerConfig;

      const isSingleQuestion =
        dataToDuplicate.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
        dataToDuplicate.questionData.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN;
      const isMultipleQuestion =
        dataToDuplicate.questionData.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER;

      let newSaAnswer: SingleAnswerConfigAnswerProps[] = [];
      let newMaAnswer: MultipleAnswerConfigAnswerProps[] = [];
      if (isSingleQuestion && dataToDuplicateSingleAnswerConfig) {
        const isGotoRequired = dataToDuplicateSingleAnswerConfig.gotoRequired;
        // duplicate SA question's answer
        newSaAnswer = dataToDuplicateSingleAnswerConfig.answers.map((answer) => ({
          ...answer,
          ...(isGotoRequired && {
            goto: answer.goto,
          }),
        }));
      } else if (isMultipleQuestion && dataToDuplicateMultipleAnswerConfig) {
        // duplicate MA question's answer
        newMaAnswer = dataToDuplicateMultipleAnswerConfig.answers;
      }

      const insertIdx = duplicateIdx + 1;
      newContentsList.splice(insertIdx, 0, {
        contentType: CONTENT_TYPE.PAGE_BREAK,
        pageBreakItemProps: {
          isGotoEndEnabled,
          isGotoEndChecked,
        },
        key: uuidv4(),
      });

      newContentsList.splice(insertIdx + 1, 0, {
        ...newContentsList[duplicateIdx],
        key: newId,
        isActive: true,
        questionBoxProps: {
          ...dataToDuplicate,
          questionData: {
            ...dataToDuplicate.questionData,
            ...(dataToDuplicateSingleAnswerConfig && {
              singleAnswerConfig: {
                ...dataToDuplicateSingleAnswerConfig,
                ...{ answers: newSaAnswer },
              },
            }),
            ...(dataToDuplicateMultipleAnswerConfig && {
              multipleAnswerConfig: {
                ...dataToDuplicateMultipleAnswerConfig,
                ...{ answers: newMaAnswer },
              },
            }),
            id: newId,
          },
        },
      });

      const updatedContentsList: OriginalFormUiDisplayProps[] = newContentsList.map(
        (item, currentIdx) => {
          const newItem = {
            ...item,
            isActive: false,
          };

          const newItemQuestionBoxProps = newItem.questionBoxProps;

          if (currentIdx === duplicateIdx) {
            return {
              ...newItem,
              isActive: true,
              ...(newItemQuestionBoxProps && {
                questionBoxProps: {
                  ...newItemQuestionBoxProps,
                  questionData: {
                    ...dataToDuplicate.questionData,
                    ...(dataToDuplicateSingleAnswerConfig && {
                      singleAnswerConfig: {
                        ...dataToDuplicateSingleAnswerConfig,
                        ...{ answers: newSaAnswer },
                      },
                    }),
                    ...(dataToDuplicateMultipleAnswerConfig && {
                      multipleAnswerConfig: {
                        ...dataToDuplicateMultipleAnswerConfig,
                        ...{ answers: newMaAnswer },
                      },
                    }),
                  },
                },
              }),
            };
          }

          return newItem;
        }
      );

      const newSurveyJson = createNewQuestionsContentSectionState(updatedContentsList);
      updateSurveyJson(newSurveyJson);
    }
  };

  const deleteQuestion = (toDeleteIdx: number) => {
    const existingContentList = [...contentsList];
    if (
      existingContentList[toDeleteIdx].contentType === CONTENT_TYPE.QUESTION_BOX &&
      getQuestionsCountWithoutBasicInfo() === ORIGINAL_FORM_VALIDATION.QUESTION_MIN_SIZE
    ) {
      setValidationErrorModalMsg(t('registration.originalForm.validationErrorModal.minQuestion'));
      return;
    }

    setIsQuestionDeleteModalShow(true);
    setDeleteIdx(toDeleteIdx);
  };

  const confirmQuestionDelete = () => {
    setIsQuestionDeleteModalShow(false);

    const newContentsList = [...contentsList];

    const { questionBoxProps } = newContentsList[deleteIdx];
    if (questionBoxProps) {
      const deleteQuestionId = questionBoxProps.questionData.id;

      if (
        newContentsList[deleteIdx + 1] &&
        newContentsList[deleteIdx + 1].contentType === CONTENT_TYPE.PAGE_BREAK &&
        !newContentsList[deleteIdx - 1]
      ) {
        newContentsList.splice(deleteIdx, 2);
      } else if (
        newContentsList[deleteIdx + 1] &&
        newContentsList[deleteIdx + 1].contentType === CONTENT_TYPE.PAGE_BREAK &&
        newContentsList[deleteIdx - 1].contentType === CONTENT_TYPE.PAGE_BREAK
      ) {
        newContentsList.splice(deleteIdx, 2);
      } else if (
        !newContentsList[deleteIdx + 1] &&
        newContentsList[deleteIdx - 1] &&
        newContentsList[deleteIdx - 1].contentType === CONTENT_TYPE.PAGE_BREAK
      ) {
        newContentsList.splice(deleteIdx - 1, 2);
      } else {
        newContentsList.splice(deleteIdx, 1);
      }

      // Determine new active Idx
      let isActiveIdx = newContentsList.findIndex((content) => content.isActive);
      if (
        newContentsList[deleteIdx] &&
        newContentsList[deleteIdx].contentType !== CONTENT_TYPE.PAGE_BREAK
      ) {
        isActiveIdx = deleteIdx;
      } else if (
        newContentsList[deleteIdx - 1] &&
        newContentsList[deleteIdx - 1].contentType !== CONTENT_TYPE.PAGE_BREAK
      ) {
        isActiveIdx = deleteIdx - 1;
      }

      setActiveElementIdx(isActiveIdx);

      // If goto is set to question id that is being deleted, set goto to empty
      const updatedContentsList: OriginalFormUiDisplayProps[] = newContentsList.map((content) => {
        const contentQuestionBox = content.questionBoxProps;

        if (
          contentQuestionBox &&
          contentQuestionBox.questionData?.singleAnswerConfig &&
          contentQuestionBox.questionData.singleAnswerConfig.answers
        ) {
          const answersList = contentQuestionBox.questionData.singleAnswerConfig.answers.map(
            (answer) => {
              if (answer.goto === deleteQuestionId) {
                return {
                  ...answer,
                  goto: '',
                };
              }
              return answer;
            }
          );

          return {
            ...content,
            questionBoxProps: {
              ...contentQuestionBox,
              questionData: {
                ...contentQuestionBox.questionData,
                singleAnswerConfig: {
                  ...contentQuestionBox.questionData.singleAnswerConfig,
                  answers: answersList,
                },
              },
            },
          };
        }
        return content;
      });

      const newSurveyJson = createNewQuestionsContentSectionState(updatedContentsList);
      updateSurveyJson(newSurveyJson);
    }
  };

  const isNgWordExists = (label: string, ngWords: Array<DraftNgWordsData>) => {
    for (let i = 0; i < ngWords.length; i += 1) {
      if (label.includes(ngWords[i].word)) {
        const ngWord = ngWords[i].word;
        const ngWordIndex = label.indexOf(ngWord);

        const startIndex = Math.max(0, ngWordIndex - 30);
        const endIndex = Math.min(label.length, ngWordIndex + ngWord.length + 30);

        const nearbyText = label.slice(startIndex, endIndex);

        const { exclusions } = ngWords[i];

        for (let j = 0; j < exclusions.length; j += 1) {
          const exclusion = exclusions[j];
          if (nearbyText.includes(exclusion)) {
            return false;
          }
        }

        return true;
      }
    }

    return false;
  };

  const validateFormContent = (ngWords: Array<DraftNgWordsData>) => {
    if (!title || !title.trim() || !validateUtf8mb4(title)) {
      setIsFormValid(false);
      return false;
    }

    if (!validateUtf8mb4(description)) {
      setIsFormValid(false);
      return false;
    }

    if (isNgWordExists(title, ngWords) || isNgWordExists(description, ngWords)) {
      setIsFormValid(false);
      return false;
    }

    let initialSectionCount = originalForm.questionsContent.description !== '' ? 1 : 0;
    let isBasicInfoActive = false;
    Object.keys(originalForm.questionsContent.basicInfo).forEach((basicInfoKey) => {
      isBasicInfoActive =
        isBasicInfoActive ||
        (originalForm.questionsContent.basicInfo as Record<string, BasicInfoPropsContent>)[
          basicInfoKey
        ].isActive;
    });
    initialSectionCount += isBasicInfoActive ? 1 : 0;

    if (
      !originalForm.questionsContent.sections ||
      originalForm.questionsContent.sections.length + initialSectionCount >
        ORIGINAL_FORM_VALIDATION.SECTION_MAX_SIZE
    ) {
      console.log('Section Count invalid.');
      setIsFormValid(false);
      return false;
    }

    if (getQuestionsCountWithBasicInfo() > ORIGINAL_FORM_VALIDATION.ALL_QUESTION_MAX_SIZE) {
      console.log('Total Question Count Across all sections invalid');
      setIsFormValid(false);
      return false;
    }

    let totalBillingQuestionCount = 0;
    originalForm.questionsContent.sections?.forEach((section: SectionProps) => {
      section.questions?.forEach((question: QuestionProps) => {
        if (question.type !== QUESTION_TYPE_ID.TEXT) totalBillingQuestionCount += 1;
      });
    });

    if (totalBillingQuestionCount === 0) {
      setIsFormValid(false);
      return false;
    }

    for (
      let sectionIdx = 0;
      sectionIdx < originalForm.questionsContent.sections.length;
      sectionIdx++
    ) {
      const section = originalForm.questionsContent.sections[sectionIdx];

      if (
        !section.questions ||
        section.questions.length === 0 ||
        section.questions.length > ORIGINAL_FORM_VALIDATION.QUESTION_MAX_SIZE
      ) {
        console.log('Question Count invalid.');
        setIsFormValid(false);
        return false;
      }

      for (let questionIdx = 0; questionIdx < section.questions.length; questionIdx++) {
        const question = section.questions[questionIdx];
        const questionLabel = removeTagsOnQuestionLabel(question.label);
        if (
          !questionLabel ||
          questionLabel.trim().length === 0 ||
          questionLabel.length > ORIGINAL_FORM_VALIDATION.QUESTION_LABEL_MAX_SIZE
        ) {
          console.log('Question Label Length invalid.');
          setIsFormValid(false);
          return false;
        }

        if (isNgWordExists(questionLabel, ngWords)) {
          console.log('Question Label contains NG words');
          setIsFormValid(false);
          return false;
        }

        if (!validateUtf8mb4(questionLabel)) {
          console.log('Question Label characters invalid.');
          setIsFormValid(false);
          return false;
        }
        // Validation for SA question type
        if (
          question.type === QUESTION_TYPE_ID.SINGLE_ANSWER ||
          question.type === QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN
        ) {
          if (
            !question.singleAnswerConfig ||
            !question.singleAnswerConfig.answers ||
            question.singleAnswerConfig.answers.length === 0 ||
            question.singleAnswerConfig.answers.length > ORIGINAL_FORM_VALIDATION.ANSWER_MAX_SIZE
          ) {
            console.log('SA: Answer Count invalid.');
            setIsFormValid(false);
            return false;
          }

          const { answers } = question.singleAnswerConfig;
          let isValidGotoRequired = false;
          for (let answerIdx = 0; answerIdx < answers.length; answerIdx++) {
            const answer = answers[answerIdx];
            const answerLabel = removeTagsOnAnswerLabel(answer.label);

            if (
              !answerLabel ||
              answerLabel.trim().length === 0 ||
              answerLabel.length > ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE
            ) {
              console.log('SA: Answer Label Length invalid.');
              setIsFormValid(false);
              return false;
            }

            if (isNgWordExists(answerLabel, ngWords)) {
              console.log('Answer Label contains NG words');
              setIsFormValid(false);
              return false;
            }

            if (!validateUtf8mb4(answerLabel)) {
              console.log('Answer Label characters invalid.');
              setIsFormValid(false);
              return false;
            }

            if (
              question.singleAnswerConfig.gotoRequired &&
              answer.goto.trim().length > 0 &&
              !isValidGotoRequired
            ) {
              isValidGotoRequired = true;
            }

            if (answer.goto && section.questions && section.questions.length !== 1) {
              console.log('SA: Go to configuration invalid.');
              setIsFormValid(false);
              return false;
            }
          }

          if (question.singleAnswerConfig.gotoRequired && !isValidGotoRequired) {
            console.log('SA: Go to configuration empty.');
            setIsFormValid(false);
            return false;
          }
        }

        if (question.type === QUESTION_TYPE_ID.MULTIPLE_ANSWER) {
          if (
            !question.multipleAnswerConfig ||
            !question.multipleAnswerConfig.answers ||
            question.multipleAnswerConfig.answers.length === 0 ||
            question.multipleAnswerConfig.answers.length > ORIGINAL_FORM_VALIDATION.ANSWER_MAX_SIZE
          ) {
            console.log('MA: Answer Count invalid.');
            setIsFormValid(false);
            return false;
          }

          const { answers } = question.multipleAnswerConfig;
          for (let answersIdx = 0; answersIdx < answers.length; answersIdx++) {
            const answer = answers[answersIdx];
            const answerLabel = removeTagsOnAnswerLabel(answer.label);

            if (
              answerLabel.trim().length === 0 ||
              answerLabel.length > ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE
            ) {
              console.log('MA: Answer label length invalid.');
              setIsFormValid(false);
              return false;
            }

            if (isNgWordExists(answerLabel, ngWords)) {
              console.log('MA: Answer Label contains NG words');
              setIsFormValid(false);
              return false;
            }

            if (!validateUtf8mb4(answerLabel)) {
              console.log('Answer Label characters invalid.');
              setIsFormValid(false);
              return false;
            }
          }
        }
      }
    }

    setIsFormValid(true);
    return true;
  };

  const checkValidationAnswers = (
    dataConfig: SingleAnswerConfigProps | MultipleAnswerConfigProps | undefined
  ) => {
    let list = dataConfig?.answers;

    if (list && list.length > 0) {
      let listAnswers = JSON.parse(JSON.stringify(dataConfig?.answers));

      listAnswers = listAnswers.map(
        (answer: MultipleAnswerConfigAnswerProps | SingleAnswerConfigAnswerProps) => {
          const tempAnswer = { ...answer };
          const newAnswer = removeTagsOnAnswerLabel(tempAnswer.label);

          if (
            !newAnswer ||
            newAnswer.trim().length === 0 ||
            newAnswer.length > ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE ||
            isNgWordExists(newAnswer, ngWordsList) ||
            !validateUtf8mb4(newAnswer)
          ) {
            tempAnswer.isValid = false;
          } else {
            tempAnswer.isValid = true;
          }

          return tempAnswer;
        }
      );

      list = listAnswers;
    }

    return list;
  };

  const showValidation = () => {
    const questionListRef = [...contentsList];
    let tempContentsList: OriginalFormUiDisplayProps[] = JSON.parse(
      JSON.stringify(questionListRef)
    );

    tempContentsList = tempContentsList.map((ele) => {
      const tmpEle = { ...ele };

      if (tmpEle.contentType !== CONTENT_TYPE.PAGE_BREAK && tmpEle.questionBoxProps) {
        const qBoxProps = tmpEle.questionBoxProps;
        qBoxProps.isValidate = true;

        const qData = tmpEle.questionBoxProps.questionData;
        const newDataLabel = removeTagsOnQuestionLabel(qData.label);
        // Check input question
        if (
          !newDataLabel ||
          newDataLabel.trim().length === 0 ||
          newDataLabel.length > ORIGINAL_FORM_VALIDATION.QUESTION_LABEL_MAX_SIZE ||
          isNgWordExists(newDataLabel, ngWordsList) ||
          !validateUtf8mb4(newDataLabel)
        ) {
          qData.isQuestionValid = false;
        } else {
          qData.isQuestionValid = true;
        }

        // Check input answers
        qData.isGotoSectionValid = true;
        switch (qData.type) {
          case QUESTION_TYPE_ID.SINGLE_ANSWER:
          case QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN:
            if (qData.singleAnswerConfig !== undefined) {
              const newAnswers = checkValidationAnswers(qData.singleAnswerConfig);
              if (newAnswers) {
                qData.singleAnswerConfig.answers = newAnswers as SingleAnswerConfigAnswerProps[];
              }

              // Count questions with same section
              if (qData.singleAnswerConfig.gotoRequired) {
                if (
                  tempContentsList.filter(
                    (content) =>
                      content.questionBoxProps?.currentSectionCount ===
                      qBoxProps.currentSectionCount
                  ).length > 1
                ) {
                  qData.isGotoSectionValid = false;
                } else {
                  // check goto required valid
                  qData.isGotoSectionValid = qData.singleAnswerConfig.answers.some(
                    (answer) => answer?.goto?.trim().length > 0
                  );
                }
              }
            }
            break;
          case QUESTION_TYPE_ID.MULTIPLE_ANSWER:
            if (qData.multipleAnswerConfig !== undefined) {
              const newAnswers = checkValidationAnswers(qData.multipleAnswerConfig);
              if (newAnswers) {
                qData.multipleAnswerConfig.answers =
                  newAnswers as MultipleAnswerConfigAnswerProps[];
              }
            }
            break;
          default:
        }

        qBoxProps.questionData = qData;
      }

      return tmpEle;
    });

    setContentsList(tempContentsList);

    let titleAndDescInvalid = false;
    if (!title || !title.trim() || !validateUtf8mb4(title) || isNgWordExists(title, ngWordsList)) {
      setIsTitleValid(false);
      titleAndDescInvalid = true;
    }
    if (isNgWordExists(description, ngWordsList) || !validateUtf8mb4(description)) {
      setIsDescValid(false);
      titleAndDescInvalid = true;
    }

    if (titleAndDescInvalid) {
      titleAndDescRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    } else {
      errorIdxRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  };

  const onCreateComplete = async () => {
    if (originalForm.id) {
      const questionsContent = handleRemoveIsValidAnswerProperty(originalForm.questionsContent);
      const { payload: updateResponse } = (await dispatch(
        updateQuestionsContent({
          surveyId: originalForm.id,
          questionsContent,
        })
      )) as unknown as PayloadAction<
        RestApiResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;

      if (updateResponse.status === GMOASK_STATUS.OK) {
        const { payload: finalizeResponse } = (await dispatch(
          finalizeOriginalFormSurvey({
            surveyId: originalForm.id,
          })
        )) as unknown as PayloadAction<
          RestApiResponse,
          string,
          { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
          never
        >;

        if (finalizeResponse.status === GMOASK_STATUS.OK) {
          navigate(ROUTES.USER.SURVEY.replace(':id', originalForm.id.toString()), {
            state: { referrer: window.location.pathname },
          });
        }
      }
    }
  };

  const submitForm = () => {
    setIsTitleValid(true);
    setIsDescValid(true);
    setIsFormValid(true);

    if (validateFormContent(ngWordsList)) {
      if (
        user.screenVisibilitySettings.find(
          (ele: number) =>
            ele ===
            SCREEN_VISIBILITY_SETTINGS_IDS[
              SCREEN_VISIBILITY_SETTINGS.SURVEY_REGISTER_NG_CHECKLIST_HIDDEN
            ]
        )
      ) {
        setOpenNgChecklistModals(false);
        onCreateComplete();
      } else {
        showValidation();
        setOpenNgChecklistModals(true);
      }
    } else {
      showValidation();
    }
  };

  const showPreviewPage = async () => {
    if (validateFormContent(ngWordsList)) {
      setIsTitleValid(true);
      setIsDescValid(true);
      setIsFormValid(true);

      const { payload: previewContent } = (await dispatch(
        getPreviewDraft({
          surveyId: originalForm.id,
        })
      )) as unknown as PayloadAction<
        PreviewSurveyResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;

      if (previewContent.status === GMOASK_STATUS.OK) {
        const { redirectUrl } = previewContent.result;
        window.open(redirectUrl, '_blank')?.focus();
      }
    } else {
      showValidation();
    }
  };

  useEffect(() => {
    setShowNgChecklistModal(
      !user.screenVisibilitySettings.some(
        (visibilitySetting: number) =>
          visibilitySetting ===
          SCREEN_VISIBILITY_SETTINGS_IDS[
            SCREEN_VISIBILITY_SETTINGS.SURVEY_REGISTER_NG_CHECKLIST_HIDDEN
          ]
      )
    );
  }, [user.screenVisibilitySettings]);

  useEffect(() => {
    const init = async () => {
      await dispatch(fetchUserScreenVisibilitySettings());

      const surveyId = window.location.pathname.split(
        ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM_MANUAL.replace(':id', '')
      )[1];
      const { payload: getResponse } = (await dispatch(
        getQuestionsContent({ surveyId: Number(surveyId) })
      )) as unknown as PayloadAction<
        GetQuestionsContentResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;

      if (getResponse.status === GMOASK_STATUS.OK) {
        dispatch({
          type: 'originalForm/updated',
          payload: {
            id: getResponse.result.id,
            name: getResponse.result.name,
            questionsContent: getResponse.result.questionsContent,
          },
        });

        setTitle(getResponse.result.questionsContent.title);
        setDescription(
          unescapeQuestionsContentLabel(getResponse.result.questionsContent.description)
        );
        setBasicInfo(getResponse.result.questionsContent.basicInfo);
        getContentsListFromQuestionsContent(getResponse.result.questionsContent);
        setNewQuestionIds(getResponse.result.questionsContent);
      }
      Object.keys(basicInfo).forEach((item) => {
        if ((basicInfo as Record<string, BasicInfoPropsContent>)[item].isActive) {
          if (item === 'age') setIsCheckAge(false);
          if (item === 'gender') setIsCheckGender(false);
          if (item === 'area') setIsCheckArea(false);
        }
      });

      // Get Draft NG Words
      const { payload: draftNgWordsResponse } = (await dispatch(
        getDraftNgWords()
      )) as unknown as PayloadAction<
        GetDraftNgWordsResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;

      // Validate the form to check for draftNgWords
      const ngWords = draftNgWordsResponse.result;
      setNgWordsList(ngWords);
    };

    init();
  }, []);

  const handleIsActiveElementIdx = (
    idx: number,
    ref: MutableRefObject<HTMLDivElement | null>,
    isInvalid = false
  ) => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });

    if (isInvalid) {
      setErrorIdxRef(ref);
    } else {
      // Set isActive to false for all elements except idx
      const newContentsList = contentsList.map((content, contentIdx) => {
        if (contentIdx === idx) {
          return { ...content, isActive: true };
        }
        return { ...content, isActive: false };
      });

      setActiveIdxRef(ref);
      setContentsList(newContentsList);
      setActiveElementIdx(idx);
    }
  };

  /* istanbul ignore next */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      // if a section is set with 「エンドページに遷移」, that section can only have 1 question
      const activeItem = contentsList.filter((item) => item.key === active?.id)[0];
      const overItem = contentsList.filter((item) => item.key === over?.id)[0];

      let sectionCountOver: number | undefined;
      let nextOverItem = null;
      if (overItem.contentType === CONTENT_TYPE.PAGE_BREAK) {
        const idxActiveItem = contentsList.indexOf(activeItem);
        const idxOverItem = contentsList.indexOf(overItem);

        if (idxActiveItem < idxOverItem) {
          nextOverItem = contentsList[idxOverItem + 1];
          sectionCountOver = nextOverItem.questionBoxProps?.currentSectionCount;
        } else {
          nextOverItem = contentsList[idxOverItem - 1];
          sectionCountOver = nextOverItem.questionBoxProps?.currentSectionCount;
        }
      } else {
        sectionCountOver = overItem.questionBoxProps?.currentSectionCount;
      }

      const totalQuestionCountOver = contentsList.filter(
        (item) =>
          item.contentType === CONTENT_TYPE.QUESTION_BOX &&
          item.questionBoxProps?.currentSectionCount === sectionCountOver
      ).length;

      if (
        activeItem.contentType === CONTENT_TYPE.QUESTION_BOX &&
        !activeItem.questionBoxProps?.questionData.singleAnswerConfig?.gotoRequired &&
        ((overItem.contentType === CONTENT_TYPE.PAGE_BREAK &&
          nextOverItem !== null &&
          nextOverItem.pageBreakItemProps.isGotoEndChecked) ||
          (overItem.contentType !== CONTENT_TYPE.PAGE_BREAK &&
            overItem.pageBreakItemProps.isGotoEndChecked)) &&
        totalQuestionCountOver === 1
      ) {
        setValidationErrorModalMsg(
          t('registration.originalForm.validationErrorModal.gotoEndSectionError')
        );
        return;
      }

      const tmpQuestionIds = dndIds.map((ele) => ele);
      const activeIndex = tmpQuestionIds.indexOf(active.id as string);
      let overIndex = tmpQuestionIds.indexOf(over.id as string);
      const tmpMovementIds = arrayMove([...tmpQuestionIds], activeIndex, overIndex);

      const activeIsGoToQuestion = contentsList.some(
        (item) =>
          item.questionBoxProps?.questionData.singleAnswerConfig?.gotoRequired &&
          item.key === active?.id
      );

      const gotoIds = contentsList
        .filter((item) => item.questionBoxProps?.questionData.singleAnswerConfig?.gotoRequired)
        .flatMap((item) => item.questionBoxProps?.questionData.singleAnswerConfig?.answers ?? [])
        .filter((answer) => answer.goto)
        .map((answer) => answer.goto);

      const pageBreakIds = contentsList
        .filter((item) => item.contentType === CONTENT_TYPE.PAGE_BREAK)
        .map((item) => item.key);
      const tmpActiveIndex = tmpMovementIds.indexOf(active.id as string);

      // Unable drag GoTo question to the bottom of refer-Goto question
      if (activeIsGoToQuestion) {
        let isValidIndex = true;
        gotoIds?.forEach((gotoId) => {
          if (isValidIndex && gotoId && tmpMovementIds.indexOf(gotoId) < tmpActiveIndex) {
            isValidIndex = false;
          }
        });
        if (!isValidIndex) {
          setValidationErrorModalMsg(
            t('registration.originalForm.validationErrorModal.draggableWithGotoOverRange')
          );
          return;
        }
      }

      // Check GoTO is the first question of section
      if (gotoIds?.includes(active.id as string)) {
        const questionGoToIds = contentsList
          .filter(
            (item) =>
              item.questionBoxProps?.questionData.singleAnswerConfig?.gotoRequired &&
              item.questionBoxProps?.questionData.singleAnswerConfig?.answers?.some(
                (answer) => answer.goto === active.id
              )
          )
          .map((item) => item.questionBoxProps?.questionData.id);
        let isValidIndex = true;
        questionGoToIds?.forEach((questionGoToId) => {
          if (
            isValidIndex &&
            questionGoToId &&
            tmpMovementIds.indexOf(questionGoToId) > tmpActiveIndex
          ) {
            isValidIndex = false;
          }
        });
        if (!isValidIndex) {
          setValidationErrorModalMsg(
            t('registration.originalForm.validationErrorModal.draggableWithGotoOverRange')
          );
          return;
        }

        if (!pageBreakIds.includes(tmpMovementIds[tmpActiveIndex - 1])) {
          setValidationErrorModalMsg(
            t('registration.originalForm.validationErrorModal.draggableWithGoto')
          );
          return;
        }
      }
      if (gotoIds?.includes(over.id as string)) {
        const tmpOverIndex = tmpMovementIds.indexOf(over.id as string);
        if (!pageBreakIds.includes(tmpMovementIds[tmpOverIndex - 1])) {
          setValidationErrorModalMsg(
            t('registration.originalForm.validationErrorModal.draggableWithGoto')
          );
          return;
        }
      }

      const newContentsList = arrayMove([...contentsList], activeIndex, overIndex);
      setActiveElementIdx(overIndex);

      // Add page break if it is question box goto is checked
      if (
        newContentsList[overIndex].contentType === CONTENT_TYPE.QUESTION_BOX &&
        newContentsList[overIndex].questionBoxProps?.questionData.singleAnswerConfig?.gotoRequired
      ) {
        newContentsList.splice(overIndex, 0, {
          contentType: CONTENT_TYPE.PAGE_BREAK,
          pageBreakItemProps: {
            isGotoEndChecked: false,
            isGotoEndEnabled: false,
          },
          key: uuidv4(),
        });
        newContentsList.splice(overIndex + 2, 0, {
          contentType: CONTENT_TYPE.PAGE_BREAK,
          pageBreakItemProps: {
            isGotoEndChecked: false,
            isGotoEndEnabled: false,
          },
          key: uuidv4(),
        });
        overIndex += 1;
      }

      newContentsList.map((content: OriginalFormUiDisplayProps, idx: number) => {
        if (content?.contentType === CONTENT_TYPE.PAGE_BREAK) {
          if (idx === 0) {
            // Remove page break if it comes before the first question

            if (newContentsList[idx + 1].contentType === CONTENT_TYPE.PAGE_BREAK) {
              newContentsList.splice(idx + 1, 1);
              overIndex -= 1;
            }
            newContentsList.splice(idx, 1);
            setActiveElementIdx(overIndex - 1);
          } else if (idx === newContentsList.length - 1)
            // Remove page break if it is the last element

            newContentsList.splice(idx, 1);
          else if (newContentsList[idx + 1].contentType === CONTENT_TYPE.PAGE_BREAK) {
            // Remove page break if it is in the duplicate page break

            newContentsList.splice(idx + 1, 1);
            if (idx < overIndex) setActiveElementIdx(overIndex - 1);
          } else if (newContentsList[idx - 1].contentType === CONTENT_TYPE.PAGE_BREAK) {
            // Remove page break if it is in the duplicate page break

            newContentsList.splice(idx, 1);
            if (idx < overIndex) setActiveElementIdx(overIndex - 1);
          }
        }
        return newContentsList;
      });

      // Get question ID on first question of section
      let isPageBreak = true;
      const dragGotoQuestionIds: Array<string> = newContentsList
        .map((content: OriginalFormUiDisplayProps) => {
          if (
            isPageBreak &&
            content.contentType === CONTENT_TYPE.QUESTION_BOX &&
            content.questionBoxProps?.questionData
          ) {
            isPageBreak = false;
            return content.questionBoxProps.questionData.id;
          }

          if (content.contentType === CONTENT_TYPE.PAGE_BREAK) {
            isPageBreak = true;
          }

          return '';
        })
        .filter((id: string) => id !== '');

      // Update the Goto values
      const updatedContentsList: OriginalFormUiDisplayProps[] = newContentsList.map(
        (content: OriginalFormUiDisplayProps) => {
          const contentQuestionBox = content.questionBoxProps;

          if (
            contentQuestionBox &&
            contentQuestionBox.questionData?.singleAnswerConfig &&
            contentQuestionBox.questionData.singleAnswerConfig.answers
          ) {
            const answersList = contentQuestionBox.questionData.singleAnswerConfig.answers.map(
              (answer) => {
                if (answer.goto !== 'END' && !dragGotoQuestionIds.includes(answer.goto)) {
                  return {
                    ...answer,
                    goto: '',
                  };
                }
                return answer;
              }
            );

            return {
              ...content,
              questionBoxProps: {
                ...contentQuestionBox,
                questionData: {
                  ...contentQuestionBox.questionData,
                  singleAnswerConfig: {
                    ...contentQuestionBox.questionData.singleAnswerConfig,
                    answers: answersList,
                  },
                },
              },
            };
          }
          return content;
        }
      );

      const newSurveyJson = createNewQuestionsContentSectionState(updatedContentsList);
      updateSurveyJson(newSurveyJson);
    }
  };

  const getQuestionDeleteLabel = () => {
    const questionDeleteLabel =
      contentsList[deleteIdx].questionBoxProps?.questionIdx ?? /* istanbul ignore next */ 0;

    return contentsList[deleteIdx].contentType === CONTENT_TYPE.QUESTION_BOX
      ? `Q${questionDeleteLabel + 1}`
      : '説明';
  };

  useEffect(() => {
    setDndIds(contentsList.map((content) => content.key));
  }, [contentsList]);

  return (
    <>
      <ProgressTracker
        width="618px"
        labels={(PROGRESS_TRACKER_LABELS as Record<string, Array<string>>)[
          PROGRESS_TYPE.SURVEY
        ].map((label) => t(label))}
        currentNum={SURVEY_PROGRESSES[ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION]}
      />

      <SurveyRegisterForm data-testid="surveyRegisterForm">
        {links !== null && <PageLinkBar links={links} />}
        <TitleWrapper>
          <Box
            data-testid="original-form-label-box"
            sx={{
              fontSize: '13px',
              width: '140px',
              height: '25px',
              padding: '5px 9px',
              border: `1px solid ${PALETTE.border.frame}`,
              background: '#FFFFFF',
              color: PALETTE.text.main,
              fontFamily: BASE_FONT_FAMILY,
            }}
          >
            {t('survey.originalFormLabel')}
          </Box>
          <Title>{t('registration.originalForm.manual.pageTitle')}</Title>
          <SubTitle>{t('registration.originalForm.manual.subTitle')}</SubTitle>
          <HelpLink
            data-testid="uiParts-NgCheckTitleHeader-helpLink"
            warnText={t('appCommon.forbiddenText')}
            warnTop="-3px"
            warnRight="0"
            warnBackground={PALETTE.secondary.subButton}
            warnBorder={`1px solid ${PALETTE.border.frame}`}
            warnBorderRadius="30px"
            warnLink={ROUTES.COMMON.FORBIDDEN_TEXT}
          />
        </TitleWrapper>

        {/* Survey Info Box */}
        <div style={{ display: 'flex' }}>
          <div>
            {/* Survey Title And Description Box */}
            <ContentBox
              ref={titleAndDescRef}
              data-testid="surveyInfoBox"
              $isValid={isTitleValid && isDescValid}
            >
              <SurveyInfoBoxNotes>
                {t('registration.originalForm.manual.surveyInfoBox.surveyInfo')}
              </SurveyInfoBoxNotes>
              <InputValidationErrorTooltip
                error={!isTitleValid}
                message={t(getOriginalFormsValidationMsg(title))}
              >
                <SurveyTitle>
                  <StyledTextField
                    $isTitleValid={isTitleValid}
                    variant="standard"
                    fullWidth
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                    }}
                    onBlur={(e) => {
                      setTitle(e.target.value);
                      updateSurveyJson({
                        ...originalForm.questionsContent,
                        title: e.target.value,
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        submitForm();
                      }
                    }}
                    inputProps={{
                      type: 'text',
                      'data-testid': 'surveyTitleTextField',
                      maxLength: 50, // Set the maximum character limit to 50
                    }}
                  />
                </SurveyTitle>
              </InputValidationErrorTooltip>

              <SurveyInfoBoxSubNotes data-testid="surveyInfoBoxDesc">
                {t('registration.originalForm.manual.surveyInfoBox.desc')}
              </SurveyInfoBoxSubNotes>

              <InputValidationErrorTooltip
                error={!isDescValid}
                message={
                  !validateUtf8mb4(description)
                    ? t('registration.originalForm.manual.inputBox.validationUtf8mb4Error')
                    : t('registration.originalForm.manual.inputBox.validationNgWordsError')
                }
              >
                <SurveyDesc>
                  <StyledTextField
                    $isDescValid={isDescValid}
                    variant="standard"
                    fullWidth
                    value={description}
                    onChange={(e) => {
                      setIsDescValid(true);
                      setDescription(e.target.value);
                    }}
                    onBlur={(e) => {
                      setIsDescValid(true);
                      setDescription(e.target.value);
                      updateSurveyJson({
                        ...originalForm.questionsContent,
                        description: e.target.value,
                      });
                    }}
                    onKeyDown={(e) => {
                      setIsDescValid(true);
                      if (e.key === 'Enter') {
                        submitForm();
                      }
                    }}
                    inputProps={{
                      type: 'text',
                      'data-testid': 'surveyDescTextField',
                      maxLength: 500, // Set the maximum character limit to 500
                    }}
                  />
                </SurveyDesc>
              </InputValidationErrorTooltip>
            </ContentBox>

            {/* Basic Info Box */}
            <ContentBox data-testid="basicInfoBox">
              <Flexwrap>
                <BasicInfo>
                  {t('registration.originalForm.manual.basicInfoBox.basicInfo')}
                </BasicInfo>
                <BasicInfoDesc>
                  {t('registration.originalForm.manual.basicInfoBox.desc')}
                </BasicInfoDesc>
                <BasicInfoDescNote>
                  {t('registration.originalForm.manual.basicInfoBox.descNote')}
                </BasicInfoDescNote>
              </Flexwrap>
              {Array.isArray(basicInfoChoices) &&
                basicInfoChoices.map((choice, choiceIdx) => {
                  const choiceKey = Object.keys(choice)[0];
                  return (
                    <ChoiceBox
                      $marginLeft={`${(choiceIdx === 0 ? 12 : choiceIdx * 250).toString()}px`}
                      $marginTop={choiceIdx === 0 ? '12px' : '-79px'}
                      key={choiceKey}
                    >
                      <Typography
                        sx={{
                          fontWeight: '500',
                          marginRight: '5px',
                          color: `${PALETTE.text.sub}`,
                          lineHeight: '36px',
                          textAlign: 'center',
                        }}
                        variant="bodyDefault"
                      >
                        {t(choice[choiceKey])}
                      </Typography>
                      <AnswerBox>
                        {Array.isArray(basicInfoAnswers) &&
                          basicInfoAnswers.map((answer) => {
                            const answerKey = Object.keys(answer)[0];
                            const checked = (
                              (basicInfo as Record<string, BasicInfoPropsContent>)[
                                choiceKey
                              ] as Record<string, boolean>
                            )[answerKey];
                            return (
                              <Checkbox
                                label={t(answer[answerKey])}
                                name={`${choiceKey}-${answerKey}`}
                                checked={checked}
                                handleChange={() => handleBasicInfoChange(choiceKey, answerKey)}
                                dataTestId={`${choiceKey}-${answerKey}`}
                                value={`${choiceKey}-${answerKey}`}
                                fontSize={24}
                                labelFontSize="16px"
                                key={`${choiceKey}-${answerKey}`}
                                disabled={isCheckBoxDisable(
                                  choiceKey,
                                  answerKey,
                                  (basicInfo as Record<string, BasicInfoPropsContent>)[choiceKey]
                                )}
                              />
                            );
                          })}
                      </AnswerBox>
                      {choiceIdx < 2 && <Divider orientation="vertical" sx={{ mt: -15 }} />}
                    </ChoiceBox>
                  );
                })}
            </ContentBox>

            <div>
              <DndContext
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                collisionDetection={closestCenter}
                onDragEnd={/* istanbul ignore next */ handleDragEnd}
              >
                <SortableContext items={dndIds} strategy={verticalListSortingStrategy}>
                  {contentsList.map((item, itemIdx) => {
                    let content;
                    const itemProps = item.questionBoxProps;
                    if (item.contentType === CONTENT_TYPE.QUESTION_BOX && itemProps) {
                      content = (
                        <QuestionBox
                          questionIdx={itemProps.questionIdx}
                          currentSection={itemProps.currentSectionCount}
                          totalSection={itemProps.totalSection}
                          totalQuestions={itemProps.totalQuestions}
                          questionData={itemProps.questionData}
                          id={item.key}
                          itemIdx={itemIdx}
                          questionIds={questionIds}
                          handleContentChange={updateQuestionContent}
                          handleDuplicateQuestion={duplicateQuestion}
                          handleDeleteQuestion={deleteQuestion}
                          handleIsActiveElement={handleIsActiveElementIdx}
                          isValidationExecuted={itemProps.isValidate ?? false}
                          handleFormValidationError={setValidationErrorModalMsg}
                          isActive={item.isActive}
                          key={item.key}
                          isGotoAnswerEnabled={item.questionBoxProps?.isGotoAnswerEnabled}
                        />
                      );
                    } else if (item.contentType === CONTENT_TYPE.DESCRIPTION_BOX && itemProps) {
                      content = (
                        <DescriptionArea
                          key={item.key}
                          isActive={item.isActive}
                          currentSection={itemProps.currentSectionCount}
                          totalSection={itemProps.totalSection}
                          itemIdx={itemIdx}
                          questionData={itemProps.questionData}
                          handleContentChange={updateQuestionContent}
                          handleDuplicateQuestion={duplicateQuestion}
                          handleDeleteQuestion={deleteQuestion}
                          handleIsActiveElement={handleIsActiveElementIdx}
                          isValidationExecuted={itemProps.isValidate ?? false}
                          id={item.key}
                        />
                      );
                    } else if (item.contentType === CONTENT_TYPE.PAGE_BREAK) {
                      content = (
                        <PageBreakItem
                          key={`${itemIdx}-pageBreak`}
                          onClickDelete={deletePage}
                          itemIdx={itemIdx}
                          isGotoEndEnabled={item.pageBreakItemProps.isGotoEndEnabled}
                          isGotoEndChecked={item.pageBreakItemProps.isGotoEndChecked}
                          handleGotoEndChange={handleGotoEndChange}
                          id={item.key}
                        />
                      );
                    }

                    return content;
                  })}
                </SortableContext>
              </DndContext>
            </div>
          </div>

          <div>
            <ContentControlBox data-testid="contentControlBox" $isValid={isFormValid}>
              {!isFormValid && (
                <ValidationArea data-testid="validation-error-area">
                  <WarningIcon sx={{ color: PALETTE.error.main, fontSize: '18px' }} />
                  <Typography variant="bodyDefault">
                    {t('registration.originalForm.manual.controlBox.validationError')}
                  </Typography>
                </ValidationArea>
              )}

              {/* Created */}
              <ControlBoxItem
                $isValid={isFormValid}
                data-testid="createdIcon"
                $background={PALETTE.primary.main}
                $border
                $isActive
                onClick={submitForm}
              >
                <TaskAltIcon sx={{ width: 24, height: 24, mr: 4 }} />
                <Typography variant="bodyDefault">
                  {t('registration.originalForm.manual.controlBox.created')}
                </Typography>
              </ControlBoxItem>

              {/* Question Area */}
              <ControlBoxItem
                $isValid={isFormValid}
                data-testid="newQuestionIcon"
                onClick={addNewQuestion}
              >
                <AddCircleOutlineIcon sx={{ width: 24, height: 24, mr: 4 }} />
                <Typography variant="bodyDefault">
                  {t('registration.originalForm.manual.controlBox.questionArea')}
                </Typography>
              </ControlBoxItem>

              {/* Description Area */}
              <ControlBoxItem
                $isValid={isFormValid}
                data-testid="descriptionAreaIcon"
                onClick={addDescriptionArea}
              >
                <PlaylistAddIcon sx={{ width: 24, height: 24, mr: 4 }} />
                <Typography variant="bodyDefault">
                  {t('registration.originalForm.manual.controlBox.descriptionArea')}
                </Typography>
              </ControlBoxItem>

              {/* New Page */}
              <ControlBoxItem $isValid={isFormValid} data-testid="newPageIcon" onClick={addNewPage}>
                <InsertPageBreakIcon sx={{ width: 24, height: 24, mr: 4 }} />
                <Typography variant="bodyDefault">
                  {t('registration.originalForm.manual.controlBox.newPage')}
                </Typography>
              </ControlBoxItem>

              {/* Preview */}
              <ControlBoxItem
                $isValid={isFormValid}
                data-testid="previewIcon"
                onClick={showPreviewPage}
              >
                <VisibilityIcon sx={{ width: 24, height: 24, mr: 4 }} />
                <Typography variant="bodyDefault">
                  {t('registration.originalForm.manual.controlBox.preview')}
                </Typography>
              </ControlBoxItem>
            </ContentControlBox>
          </div>
        </div>

        {showNgChecklistModal && (
          <NgChecklistModal
            open={openNgChecklistModals}
            handleClose={() => {
              setOpenNgChecklistModals(false);
            }}
            dataTestId="User-SurveyRegister-NgChecklistModal"
            formType={SURVEY_FORM_TYPE.ORIGINAL_FORM}
            handleRegist={async () => {
              setOpenNgChecklistModals(false);
              onCreateComplete();
            }}
          />
        )}

        {isQuestionDeleteModalShow && (
          <DeleteOriginalFormContentModal
            open={isQuestionDeleteModalShow}
            handleClose={() => {
              setIsQuestionDeleteModalShow(false);
              setDeleteIdx(-1);
            }}
            confirmDelete={confirmQuestionDelete}
            deleteContent={getQuestionDeleteLabel()}
          />
        )}

        {isPageDeleteModalShow && (
          <DeleteOriginalFormContentModal
            open={isPageDeleteModalShow}
            handleClose={() => {
              setIsPageDeleteModalShow(false);
              setDeleteIdx(-1);
            }}
            confirmDelete={confirmPageDelete}
            deleteContent="改ページ"
          />
        )}

        {validationErrorModalMsg && (
          <FormValidationModal
            open={validationErrorModalMsg !== ''}
            handleClose={() => {
              setValidationErrorModalMsg('');
            }}
            validationMsg={validationErrorModalMsg}
          />
        )}
      </SurveyRegisterForm>
    </>
  );
};

export default SurveyRegisterOriginalFormManual;
